<template>
  <div>
    <div class="table" ref="table" @scroll="scrolled">
      <table cellspacing="0" cellpadding="0" :class="{isCompact}" :dir="uiDirection"
             @mouseleave="hoveredColumn = null; removeHoveredColumn()">
        <thead>
        <tr>
          <slot name="thead"
                :xAxisScrolled="xAxisScrolled"
                :sortKey="sortKey"
                :showSymbolActions="showSymbolActions"
                :sortDirection="sortDirection"
                :headers="headers"
                :toggleSymbolActions="toggleSymbolActions"
                :thClick="thClick"/>
        </tr>
        </thead>
        <tbody>
        <slot name="tbody"
              :cols="headers"
              :showSymbolActions="showSymbolActions"
              :xAxisScrolled="xAxisScrolled"
              :rows="rows"
        />

        </tbody>
      </table>


    </div>
    <div class="pagination" ref="pagination" v-if="pagination">
      <div v-for="(page, pageNumber) in pages" :key="`page-${pageNumber}`" class="pageNumber"
           :class="{active: pageNumber === paginationPage}" @click="paginationPage = pageNumber">{{ pageNumber + 1 }}
      </div>
    </div>
  </div>
</template>

<script>

import Tabs from "../ui/Tabs";
import PriceRange from "../priceTags/priceRange";
import tableColsInfo from "../../dictionary/tableColsInfo";

export default {
  name: "DataGridTable",
  components: {PriceRange, Tabs},
  props: ['data', 'defaultSort', 'dontSort', 'heightDecrease', 'defaultSortDirection', 'fixedCol', 'isCompact', 'heightElements', 'loaded', 'hoveredColumns', 'scrollPosition', 'extraHeaders', 'excludeHeaders', 'pagination', 'currentPage', 'customSort'],
  data() {
    return {
      leftScroll: 0,
      hoveredColumn: null,
      xAxisScrolled: false,
      sortKey: this.defaultSort,
      sortDirection: this.defaultSortDirection,
      showSymbolActions: false,
      paginationPage: 0,
      numOfPages: 0,
      limit: 50,
      defaultHeaders: [{
        label: this.dictionary.symbolPage.symbol,
        sortValue: 'symbol',
        idKey: 'symbol'
      },
        {
          label: this.dictionary.symbolPage.price,
          sortValue: 'tradingData.changeP',
          idKey: 'changeP'
        }, {
          label: this.dictionary.symbolPage.volume,
          sortValue: 'tradingData.volume',
          idKey: 'volume'
        },
     
        {
          label: this.dictionary.symbolPage.marketCap,
          sortValue: 'tradingData.marketCap',
          idKey: 'marketCap'
        },
            {
          label: this.dictionary.symbolPage.atrPercent,
          sortValue: 'tradingData.atr.atrPercent',
          idKey: 'atrPercent'
        },
        {
          label: this.dictionary.symbolPage.atrTrend,
          sortValue: 'tradingData.atr.atrTrend',
          idKey: 'atrTrend'
        },
        {
          label: this.dictionary.symbolPage.obvTrend,
          sortValue: 'tradingData.obvTrend',
          idKey: 'obvTrend'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['5d'],
          sortValue: 'eods.5D',
          idKey: 'eod5D'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['10d'],
          sortValue: 'eods.10D',
          idKey: 'eod10D'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['1m'],
          sortValue: 'eods.1M',
          idKey: 'eod1M'
        },
        {
          label: this.dictionary.symbolPage.sma50D,
          sortValue: 'ma50D',
          idKey: 'ma50'
        },
        {
          label: this.dictionary.symbolPage.sma200D,
          sortValue: 'ma200D',
          idKey: 'ma200'
        },
        // {
        //   label: this.dictionary.symbolPage.fiftyTwoWeeksLow,
        //   sortValue: '52wlD'
        // },
        {
          label: this.dictionary.symbolPage.yearlyRange,
          sortValue: 'tradingData.yearlyRange',
          idKey: 'yearlyRange'
        },
        // {
        //   label: this.dictionary.symbolPage.fiftyTwoWeeksHigh,
        //   sortValue: '52whD'
        // },
        {
          label: this.dictionary.symbolPage.rsi,
          sortValue: 'tradingData.rsi.value',
          idKey: 'rsi'
        },
        {
          label: this.dictionary.symbolPage.macd,
          sortValue: 'tradingData.macd.histogram',
          idKey: 'macd'
        },
    
        {
          label: this.dictionary.symbolPage.eps,
          sortKey: 'tradingData.eps',
          idKey: 'eps',
        },
        {
          label: this.dictionary.symbolPage.pe,
          sortKey: 'tradingData.pe',
          idKey: 'pe',
        },
        {
          label: this.dictionary.symbolPage.sector,
          sortKey: 'sector',
          idKey: 'sector',
        }, {
          label: this.dictionary.symbolPage.industry,
          sortKey: 'industry',
          idKey: 'industry',
        }].map(column => {

        column.info = tableColsInfo[column.idKey]?.[this.systemLanguage]?.explanation ?? null;
        return column;
      })
    }
  },

  computed: {
    pages() {

      if (this.pagination) {

        if (!this.data || this.data.length === 0) return [];
        return new Array(Math.ceil(this.data.length / 50))
      }
      return null;
    },
    headers() {

      if (this.customSort?.length > 0) {
        this.customSort.forEach(sort => {

          this.defaultHeaders.find(h => h.idKey === sort.key).sortValue = sort.fn;
        });
      }

      if (this.excludeHeaders?.length > 0) {

        this.defaultHeaders = this.defaultHeaders.filter(h => !this.excludeHeaders.includes(h.idKey))
      }


      if (this.extraHeaders?.length > 0) {
        this.extraHeaders.forEach((header, index) => {

          if (this.defaultHeaders[header.position].sortValue !== header.sortValue) {
            let pushToIndex = header.position !== undefined ? header.position : index + 1;
            this.defaultHeaders.splice(pushToIndex, 0, header);
          }
        });
      }

      return this.defaultHeaders;
    },
    rows() {

      if (!this.data || this.data.length === 0) return [];

      const start = this.pagination ? (this.paginationPage) * this.limit : 0;
      let rows = this.pagination ? this.sortByKey(this.data).slice(start, start + this.limit) : this.sortByKey(this.data);

      return (!this.dontSort) ? this.sortByKey(rows) : rows;
    }
  },
  watch: {
    data(currentVal, oldVal) {

      if (currentVal.length > 0 && this.hoveredColumns) {
        this.$nextTick(() => {
          this.setHeight();
          this.setHoverColumn()
        });
      }
    }
  },
  methods: {
    setHoverColumn() {

      if (this.$refs.table) {
        this.$refs.table.querySelectorAll('th')
            .forEach((th, index) => {
              th.removeEventListener('mouseover', this.columnHover)
              th.addEventListener('mouseover', ev => this.columnHover(index))
            });

        this.$refs.table.querySelectorAll('tr')
            .forEach(tr => {

              tr.querySelectorAll('td')
                  .forEach((td, index) => {
                    td.removeEventListener('mouseover', this.columnHover)
                    td.addEventListener('mouseover', ev => this.columnHover(index))
                  });
            })
      }
    },
    setHeight() {
      if (this.data?.length > 0 && this.$refs.table) {

        this.$nextTick(() => {


          let heightElements = this.heightElements || [];
          if (heightElements.length === 0) heightElements = [this.$refs.table.parentElement];

          if (heightElements && heightElements.length > 0) {

            const tableInnerHeight = this.$refs.table.clientHeight;
            const mainContainerHeight = document.getElementById('macroNewsFeed').clientHeight;
            let totalHeightToSubtract = heightElements.reduce((final, current) => {

              final += document.getElementById(current)?.offsetHeight || 0;
              return final;
            }, 0);


            let paginationHeight = this.$refs.pagination?.clientHeight || 0;
            let tableHeight = mainContainerHeight - totalHeightToSubtract;
            this.$refs.table.style.height = ((tableHeight) - (this.heightDecrease ? this.heightDecrease : 47) - paginationHeight) + 'px';
          }
        })
      }
    },
    scrolled(ev) {
      const scrollLeftPosition = ev.target.scrollLeft;
      const scrollTopPosition = ev.target.scrollTop;

      this.$emit('scrolled', {left: scrollLeftPosition, top: scrollTopPosition})
      const scrollHeight = ev.target.scrollHeight;
      const clientHeight = ev.target.clientHeight;

      const scrollPercentage = (scrollTopPosition / (scrollHeight - clientHeight));
      this.$emit('scrollTopProgress', scrollPercentage);

      this.xAxisScrolled = Math.abs(scrollLeftPosition) > 1;

      const rows = this.$refs?.table ? this.$refs.table.querySelectorAll('tr') : [];

      for (let row of rows) {
        const tds = row.querySelectorAll('td');
        const ths = row.querySelectorAll('th');

        const cells = ths.length > 0 ? ths : tds;

        if (cells.length > 0) {
          for (let i = 0; i < this.fixedCol + 1; i++) {

            if (!cells[i].classList.contains('nonStickTd')) {
              cells[i].classList[this.xAxisScrolled ? 'add' : 'remove']('stickyCell');
              if (this.xAxisScrolled && i > 0) {

                let previousCellWidth = cells[i - 1].offsetWidth;
                cells[i].style[this.uiDirection === 'rtl' ? 'right' : 'left'] = previousCellWidth + 'px';
              }
            }
          }

          cells[this.fixedCol].classList[this.xAxisScrolled ? 'add' : 'remove']('xAxisScrolled')
        }
      }
    },
    removeHoveredColumn() {

      if (this.$refs.table) {
        this.$refs.table.querySelectorAll(`.hoveredColumn`)
            .forEach((cell) => {
              cell.classList.remove('hoveredColumn');
            });
      }
    },
    columnHover(index) {

      if (this.$refs.table) {
        this.hoveredColumn = index + 1;
        this.removeHoveredColumn();
        this.$refs.table.querySelectorAll(`th:nth-of-type(${this.hoveredColumn}), td:nth-of-type(${this.hoveredColumn})`)
            .forEach((cell) => {
              cell.classList.add('hoveredColumn');
            });
      }
    },
    sortByKey(array) {

      if (this.scrollPosition) {
        const scrollPosition = this.scrollPosition;
        this.$nextTick(() => {

          this.$refs.table.scrollLeft = scrollPosition.left;
        })
      }
      return array.sort((a, b) => {

        if (!this.sortKey) return array;

        let sortKey = null;

        if (this.sortKey instanceof Function) {
          sortKey = this.sortKey();
        } else {
          sortKey = this.sortKey;
        }
        const keys = sortKey.split('.');
        let aValue = a;
        let bValue = b;

        for (const k of keys) {
          aValue = aValue?.[k] || 0;
          bValue = bValue?.[k] || 0;
        }

        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return comparison * this.sortDirection;
      });
    },
    toggleSymbolActions() {
      this.showSymbolActions = !this.showSymbolActions;
    },
    thClick(key, thIndex) {


      if (key === this.sortKey) {
        this.sortDirection *= -1;
      } else {
        this.sortKey = key;
        this.sortDirection = 1;
      }

      this.$nextTick(() => {


        if (this.$refs.table) {
          this.$refs.table.querySelectorAll('.sorted').forEach(sorted => sorted.classList.remove('sorted'))
          this.$refs.table.querySelectorAll('.first-sorted').forEach(sorted => sorted.classList.remove('first-sorted'))
          this.$refs.table.querySelectorAll('.last-sorted').forEach(sorted => sorted.classList.remove('last-sorted'))
          this.$refs.table.querySelectorAll('th')
              .forEach((th, index) => {

                if (index === thIndex) {

                  th.classList.add('sorted')
                  th.classList.add('first-sorted')
                }
              });

          const trs = this.$refs.table.querySelectorAll('tr');
          trs.forEach((tr, trIndex) => {

            const isLast = trIndex === trs.length - 1;
            tr.querySelectorAll('td')
                .forEach((td, index) => {
                  if (index === thIndex) {
                    td.classList.add('sorted')
                    if (isLast) td.classList.add('last-sorted')
                  }
                });
          })
        }
      })
    },
    handleResize() {
      this.setHeight()
    }
  },
  created() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.setHeight();
      }, 1000)
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  }
}
</script>

<style scoped lang="scss">
@import "../../assets/style/vars";

.table {
  position: relative;
  overflow: auto;
  white-space: nowrap;
  transition: $transition;
  min-height: 30vh;

  &.fadeOutTable {
    opacity: .2;
    filter: blur(1px);
  }
}

table {
  width: 100%;
}

table ::v-deep(th) {
  text-align: left;
  background: var(--bgColor);
  width: fit-content;
  font-weight: 600;
  border-right: 1px solid var(--notficationBorderColor);
}

table[dir="rtl"] ::v-deep(th) {
  text-align: right;
  border-right: unset;
  border-left: 1px solid var(--notficationBorderColor);
}

table ::v-deep(tr) {
  background: var(--bgColor) !important;

  &:hover {
    background: var(--tableHoverColor);

    td {
      background: var(--tableHoverColor);
    }
  }
}

::v-deep(td) {
  padding: 4px 6px !important;
  border-bottom: 1px solid var(--notficationBorderColor);
  width: auto;
  font-weight: 600;
  background: var(--bgColor);
  border-right: 1px solid var(--notficationBorderColor);
  font-size: 14px;

  div, span {
    font-size: 14px;
  }

  * {
    font-size: 14px;
  }
}

table[dir="rtl"] ::v-deep(td) {
  border-right: unset;
  border-left: 1px solid var(--notficationBorderColor);

}

::v-deep(td.center) {
  text-align: center;
}

::v-deep(.linkable) {
  position: relative;

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
}

::v-deep(.symbolTd) {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 0 auto;

  a {
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

table.isCompact ::v-deep(td),
table.isCompact ::v-deep(th) {
  font-size: 14px;
}

table ::v-deep(.bidi) {
  unicode-bidi: plaintext;
  height: 21px !important;
}

::v-deep(td.trendTd) {
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
}

::v-deep(.textOverflow) {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

::v-deep(td.trendTd.green) {
  color: var(--green);

  &:before {
    background: var(--opacityGreen);
  }
}

::v-deep(td.trendTd.red) {
  color: var(--red);

  &:before {
    background: var(--opacityRed);
  }
}

::v-deep(.xAxisScrolled) {
  box-shadow: 3px 0 2px 0 rgba(0, 0, 0, 0.05);
  border-right: 1px solid var(--notficationBorderColor);
  z-index: 0;
}


table[dir="rtl"] ::v-deep(.xAxisScrolled) {
  border-right: unset;
  border-left: 1px solid var(--notficationBorderColor);
  box-shadow: -3px 0 2px 0 rgba(0, 0, 0, 0.05);
}

::v-deep(.stickyCell) {
  //background: inherit;
  position: sticky;
  left: 0;
  border-right: 1px solid var(--tableBorderColor);
  z-index: 1;
}

::v-deep(.fullLink) {
  position: relative;

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

::v-deep(.:not(.green):not(.red)) {
  background: var(--tableHoverColor) !important;
}

::v-deep(.cellHighlight) {
  background: var(--opacityPurple) !important;
  color: var(--purple) !important;
}

::v-deep(.highlightRow) {
  background: rgba(38, 49, 81, 0.35) !important;

  td {
    background: var(--lightYellowOpacity) !important;
  }
}

::v-deep(.symbolTd) {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

table[dir="rtl"] ::v-deep(.stickyCell) {
  border-right: unset;
  border-left: 1px solid var(--tableBorderColor);
  left: unset;
  right: 0;
}

thead {
  position: sticky;
  top: -1px;
  z-index: 10;
  background: var(--bgColor);
}

.tableGradient {
  height: 50px;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 1;
  background-image: linear-gradient(to bottom, rgba(255, 0, 0, 0), var(--bgColor));
  position: fixed;
}

::v-deep(.addRemoveSymbol) {
  flex: 0 0 55px !important;
  width: 45px;
  margin-left: auto;

  &[dir="rtl"] {
    margin-left: unset;
    margin-right: auto;
  }

  i {
    display: none;
  }

  @media(max-width: 660px) {

    flex: 0 0 30px !important;
    width: 30px;
    height: 20px;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    i {
      display: block;
    }

    span {
      display: none;
    }
  }
}

::v-deep(.addRemoveSymbol.fixedSixty) {
  span {
    font-size: 16px;
  }

  box-sizing: border-box;
  margin: 0;
}


::v-deep(.addRemoveSymbol.add) {
  background: var(--highlightColorOpacity);
  color: var(--highlightColor);
}

::v-deep(.addRemoveSymbol.remove) {
  background: var(--opacityRed);
  color: var(--red);
}

::v-deep(.sorted) {
  box-sizing: content-box;
  border-left: 2px solid var(--highlightColor) !important;
  border-right: 2px solid var(--highlightColor) !important;
}

::v-deep(.sorted:not(.green):not(.red)) {
  background: var(--tableHoverColor) !important;
}

::v-deep(.sorted.first-sorted) {
  border-top: 2px solid var(--highlightColor) !important;
  overflow: hidden;
}

::v-deep(.sorted.last-sorted) {
  border-bottom: 2px solid var(--highlightColor) !important;
}

.pagination {
  border-top: 1px solid var(--notficationBorderColor);
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 1rem;

  .pageNumber {
    transition: all .2s ease-out;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: var(--tableHoverColor);
    }

    &.active {
      color: var(--highlightColor);
      background: var(--highlightColorOpacity);

    }
  }
}

</style>
